<script setup>
import * as d3 from 'd3';
import { computed, onMounted, onUnmounted, ref, toRefs, watch } from 'vue';

import { useAnalysisStore } from '@/src/store/analysis';
import { useContourContainerStore } from '@/src/store/contours/contour-container-store';
import * as ContourDefinitions from '@/src/store/contours/contour-definitions';
import { useContourEventStore } from '@/src/store/contours/contour-event-store';

import { convertPointArrayToArray } from '@/src/store/contours/contour-processing/convertArrayToPointArray';
import { useViewportStateStore } from '@/src/store/contours/viewport-state-store';

import { useCurrentStudyStore } from '@/src/store/dicom-web/current-study-store';

import {
  ANNOTATION_FONT,
  CONNECTOR_STROKE,
  CONTOUR_STROKE,
  ENDPOINT_RADIUS,
  POINT_RADIUS,
  THIN_STROKE,
} from './contour-draw-constants';
import {
  appendArrow,
  appendCircle,
  appendLabel,
  appendMeasurementBadge,
  appendPath,
  appendSelectionHandle,
  applyGroupTransform,
} from './contour-svg-primitives';
import { coordinateTransformation } from './coordinateTransformation';
import {
  drawRangeCrossbars,
  getLocalizerDir2D,
  RANGE_CROSS_OPACITY,
  RANGE_CROSS_STROKE,
} from './lax-range-crossbar';

const props = defineProps({
  id: { type: String, required: true },
  active: { type: Boolean, required: true },
  currentImageUid: { type: String, required: true },
  showContours: { type: Boolean, required: true },
  showMeasurementBadges: { type: Boolean, default: true },
  canvasWidth: { type: Number, required: true },
  canvasHeight: { type: Number, required: true },
});

const emit = defineEmits(['updateContourFormat', 'updateMeasurements']);

// Getting stores:
const contourContainerStore = useContourContainerStore();
const viewportStateStore = useViewportStateStore();
const contourEventStore = useContourEventStore();
const currentStudyStore = useCurrentStudyStore();
const analysisStore = useAnalysisStore();

// Used to get current viewer:
const {
  id: viewID,
  currentImageUid: curImageID,
  canvasWidth,
  canvasHeight,
  showContours,
  showMeasurementBadges,
} = toRefs(props);

const overlay = ref(null);
const formattedContourValues = ref('');
const currentMeasurements = ref([]);
const hoveredContourKey = ref(null);
const pinnedContourKeys = ref(new Set());

const selectedMaskType = computed(() =>
  viewportStateStore.getSelectedMaskType(viewID.value),
);

const visibleGeneratedContourIds = computed(() =>
  viewportStateStore.getVisibleGeneratedContourIds(viewID.value),
);

// This provides all of the contours on the current image. Used for drawing.
const currentDrawingContours = computed(() => {
  const container = contourContainerStore.getContourContainer(curImageID.value);
  if (!container) return null;

  // Trigger generation of any whitelisted generated contour IDs for this viewport!
  const whitelistedIds = visibleGeneratedContourIds.value;
  if (whitelistedIds && whitelistedIds.length > 0) {
    whitelistedIds.forEach((contourId) => {
      container.getContour(contourId);
    });
  }

  return container.getDrawingContours() || null;
});

const {
  scaleFactor,
  imageToScreen,
  overlayTransform,
  imageToScreenMatrix,
  matrixReady,
} = coordinateTransformation({
  currentImageUid: curImageID,
  id: viewID,
  canvasWidth,
  canvasHeight,
});

const saxLvTransmuralityOffsetEndo = computed(() =>
  viewportStateStore.getSaxLvTransmuralityOffsetEndo(viewID.value),
);
const saxLvTransmuralityOffsetEpi = computed(() =>
  viewportStateStore.getSaxLvTransmuralityOffsetEpi(viewID.value),
);

function isDisplayedGeneratedContour(contourId) {
  const { label, id } = contourId;
  // exact (label + id) whitelist wins over label/offset rules
  if (isWhitelistedGeneratedId(label, id)) return true;
  return visibleGeneratedContours.value.includes(label);
}

const selectedContour = computed(() =>
  viewportStateStore.getSelectedContour(viewID.value),
);

const moveContour = computed(() =>
  viewportStateStore.getMoveContour(viewID.value),
);

const hideDrawingContour = computed(() =>
  viewportStateStore.getHideDrawingContour(viewID.value),
);

const hideDrawingContourLabel = computed(() =>
  viewportStateStore.getHideDrawingContourLabel(viewID.value),
);

const hiddenUserEditableContours = computed(() =>
  viewportStateStore.getHiddenUserEditableContours(viewID.value),
);

const visibleGeneratedContours = computed(() =>
  viewportStateStore.getVisibleGeneratedContours(viewID.value),
);

function isWhitelistedGeneratedId(label, id) {
  return visibleGeneratedContourIds.value.some(c =>
    ContourDefinitions.equalContourIds(c, { label, id }),
  );
}

const localizerDir2D = computed(() =>
  getLocalizerDir2D(viewID.value, curImageID.value, viewportStateStore, currentStudyStore),
);

function isSelected(contour) {
  return ContourDefinitions.equalContourIds(
    { label: contour.getProps().label, id: contour.getProps().id },
    selectedContour.value,
  );
}

function resolveColor(contour) {
  const { label, color } = contour.getProps();
  return ContourDefinitions.ContourLabelColor[label] || color;
}

function getContourDisplayOpacity(contour) {
  return contour.getProps().userEditable ? 1 : 0.99;
}

// `setMoveContour` stores the drag delta in image-pixel space, so it is applied
// directly here (no scaleFactor division). Returns whether this contour is the
// one being moved (used to brighten the centroid handle).
function moveDisplayContour(contourId, scaledContour, centroidPosition) {
  const mc = moveContour.value;
  if (
    mc
    && mc.label != null
    && ContourDefinitions.equalContourIds(
      { label: mc.label, id: mc.id },
      contourId,
    )
  ) {
    const dx = mc.distancePoint[0];
    const dy = mc.distancePoint[1];
    scaledContour.forEach((point) => {
      point[0] += dx;

      point[1] += dy;
    });
    if (centroidPosition) {
      centroidPosition.x += dx;

      centroidPosition.y += dy;
    }
    return true;
  }
  return false;
}

// --- per-type drawing ------------------------------------------------------

function getTopRightContourPoint(points) {
  if (!points || !points.length) return null;
  let topPt = { x: points[0][0] ?? points[0].x, y: points[0][1] ?? points[0].y };

  for (let i = 1; i < points.length; i++) {
    const pt = points[i];
    const x = pt[0] ?? pt.x;
    const y = pt[1] ?? pt.y;
    if (y < topPt.y || (y === topPt.y && x > topPt.x)) {
      topPt = { x, y };
    }
  }

  return topPt;
}

function drawClosedPolygon(group, contour, displayNumber = -1, measurement = null, badgesGroup = group) {
  const scaledContour = convertPointArrayToArray(contour.getPolygonScaled());
  const centroidPosition = contour.getCentroidScaled();
  const color = resolveColor(contour);
  const opacity = getContourDisplayOpacity(contour);

  const contourMoved = moveDisplayContour(
    { label: contour.getProps().label, id: contour.getProps().id },
    scaledContour,
    centroidPosition,
  );

  const path = appendPath(group, scaledContour, color, opacity, CONTOUR_STROKE);

  if (isSelected(contour)) {
    appendPath(
      group,
      scaledContour,
      d3.hsl(color).brighter(1),
      1,
      CONTOUR_STROKE,
    );
    appendSelectionHandle(group, centroidPosition, color, contourMoved);
  }

  if (measurement && centroidPosition) {
    if (!contourMoved) {
      const key = measurement.key;

      const onEnter = () => handleHoverContour(key);
      const onLeave = () => handleLeaveContour(key);
      const onClick = (e) => {
        e?.stopPropagation?.();
        handleTogglePinContour(key);
      };

      const badgePos = getTopRightContourPoint(scaledContour) || centroidPosition;
      const badgeText = `#${displayNumber}  ${measurement.primaryValue}`;
      const badge = appendMeasurementBadge(
        badgesGroup,
        badgePos.x,
        badgePos.y,
        badgeText,
        {
          fill: '#0f0f0f',
          fillOpacity: 0.94,
          stroke: color,
          strokeWidth: 0.75,
          fontSize: 9.5,
          textColor: '#e5e5e5',
          paddingX: 4,
          paddingY: 1.2,
          borderRadius: 2.5,
          align: 'left',
          alignY: 'center',
        },
      );

      if (badge && typeof badge.on === 'function') {
        badge.on('mouseenter', onEnter).on('mouseleave', onLeave).on('click', onClick);
        badge.selectAll('rect').on('mouseenter', onEnter).on('mouseleave', onLeave).on('click', onClick);
      }
    }
  }
  else if (displayNumber > 0 && !isSelected(contour) && !contourMoved) {
    const firstPoint = scaledContour[0];
    appendLabel(group, firstPoint[0], firstPoint[1], displayNumber);
  }
}

function drawOpenPolygon(group, contour) {
  const scaledContour = convertPointArrayToArray(contour.getPolygonScaled());
  const centroidPosition = contour.getCentroidScaled();
  const color = resolveColor(contour);
  const opacity = getContourDisplayOpacity(contour);

  const contourMoved = moveDisplayContour(
    { label: contour.getProps().label, id: contour.getProps().id },
    scaledContour,
    centroidPosition,
  );

  appendPath(group, scaledContour, color, opacity, CONTOUR_STROKE);

  if (
    contour.getProps().label !== ContourDefinitions.ContourLabel.SaxLvOpenEpi
  ) {
    const straightLine = [
      scaledContour[0],
      scaledContour[scaledContour.length - 1],
    ];
    appendPath(group, straightLine, color, 0.4, THIN_STROKE);
  }

  if (isSelected(contour)) {
    appendPath(
      group,
      scaledContour,
      d3.hsl(color).brighter(1),
      1,
      CONTOUR_STROKE,
    );
    appendSelectionHandle(group, centroidPosition, color, contourMoved);
  }

  appendCircle(
    group,
    scaledContour[0][0],
    scaledContour[0][1],
    ENDPOINT_RADIUS,
    {
      fill: color,
      opacity,
    },
  );
  appendCircle(
    group,
    scaledContour[scaledContour.length - 1][0],
    scaledContour[scaledContour.length - 1][1],
    ENDPOINT_RADIUS,
    { fill: color, opacity },
  );
}

function drawLine(group, contour, displayNumber = -1, measurement = null, badgesGroup = group) {
  const scaledContour = convertPointArrayToArray(contour.getPolygonScaled());
  const centroidPosition = contour.getCentroidScaled();
  const color = resolveColor(contour);
  const opacity = getContourDisplayOpacity(contour);
  const { userEditable } = contour.getProps();

  const contourMoved = moveDisplayContour(
    { label: contour.getProps().label, id: contour.getProps().id },
    scaledContour,
    centroidPosition,
  );

  const path = appendPath(group, scaledContour, color, opacity, CONTOUR_STROKE);

  if (contour.getProps().label === ContourDefinitions.ContourLabel.LaxLvRange) {
    drawRangeCrossbars(group, scaledContour, localizerDir2D.value, {
      color,
      opacity: RANGE_CROSS_OPACITY,
      strokeWidth: RANGE_CROSS_STROKE,
      nonScalingStroke: true,
    });
  }

  // Endpoint handles only for editable lines (generated chords stay clean).
  if (userEditable && scaledContour.length >= 2) {
    appendCircle(
      group,
      scaledContour[0][0],
      scaledContour[0][1],
      ENDPOINT_RADIUS,
      {
        fill: color,
        opacity,
      },
    );
    appendCircle(
      group,
      scaledContour[scaledContour.length - 1][0],
      scaledContour[scaledContour.length - 1][1],
      ENDPOINT_RADIUS,
      { fill: color, opacity },
    );
  }

  if (isSelected(contour)) {
    appendPath(
      group,
      scaledContour,
      d3.hsl(color).brighter(1),
      1,
      CONTOUR_STROKE,
    );
    appendSelectionHandle(group, centroidPosition, color, contourMoved);
  }

  if (measurement && scaledContour.length >= 2) {
    if (!contourMoved) {
      const p0 = scaledContour[0];
      const p1 = scaledContour[scaledContour.length - 1];
      const mx = (p0[0] + p1[0]) / 2;
      const my = (p0[1] + p1[1]) / 2;

      const key = measurement.key;
      const onEnter = () => handleHoverContour(key);
      const onLeave = () => handleLeaveContour(key);
      const onClick = (e) => {
        e?.stopPropagation?.();
        handleTogglePinContour(key);
      };

      const badgeText = measurement.primaryValue;
      const badge = appendMeasurementBadge(badgesGroup, mx, my, badgeText, {
        fill: '#0f0f0f',
        fillOpacity: 0.94,
        stroke: color,
        strokeWidth: 0.75,
        fontSize: 9.5,
        textColor: '#e5e5e5',
        paddingX: 4,
        paddingY: 1.2,
        borderRadius: 2.5,
      });

      if (badge && typeof badge.on === 'function') {
        badge.on('mouseenter', onEnter).on('mouseleave', onLeave).on('click', onClick);
        badge.selectAll('rect').on('mouseenter', onEnter).on('mouseleave', onLeave).on('click', onClick);
      }
    }
  }
  else if (displayNumber > 0 && !isSelected(contour) && !contourMoved) {
    const firstPoint = scaledContour[0];
    appendLabel(group, firstPoint[0], firstPoint[1], displayNumber, 8);
  }
}

function drawPoint(group, contour) {
  const scaledContour = convertPointArrayToArray(contour.getPolygonScaled());
  if (!scaledContour || !scaledContour.length) return;

  const color = resolveColor(contour);
  moveDisplayContour(
    { label: contour.getProps().label, id: contour.getProps().id },
    scaledContour,
    null,
  );

  if (
    contour.getProps().label
    === ContourDefinitions.ContourLabel.LaxLvEndoBasalPoint
  ) {
    const endoContourId = {
      label: ContourDefinitions.ContourLabel.LaxLvEndo,
      id: ContourDefinitions.encodeContourLabelItemId([0]),
    };
    if (
      contourContainerStore.isPresentContourContourId(
        curImageID.value,
        endoContourId,
      )
    ) {
      const endoContour = convertPointArrayToArray(
        contourContainerStore.getContour(curImageID.value, endoContourId),
      );
      const endPoint1 = endoContour[0];
      const endPoint2 = endoContour[endoContour.length - 1];

      const mc = moveContour.value;
      if (mc && mc.label === ContourDefinitions.ContourLabel.LaxLvEndo) {
        endPoint1[0] += mc.distancePoint[0];
        endPoint1[1] += mc.distancePoint[1];
        endPoint2[0] += mc.distancePoint[0];
        endPoint2[1] += mc.distancePoint[1];
      }

      appendPath(
        group,
        [endPoint1, scaledContour[0]],
        color,
        1,
        CONNECTOR_STROKE,
      );
      appendPath(
        group,
        [endPoint2, scaledContour[0]],
        color,
        1,
        CONNECTOR_STROKE,
      );
    }
  }

  appendCircle(group, scaledContour[0][0], scaledContour[0][1], POINT_RADIUS, {
    fill: color,
    stroke: 'white',
    strokeWidth: 1,
  });

  if (isSelected(contour)) {
    appendCircle(
      group,
      scaledContour[0][0],
      scaledContour[0][1],
      POINT_RADIUS + 2,
      {
        fill: 'none',
        stroke: 'white',
        strokeWidth: 1,
      },
    );
  }
}

function drawArrow(group, contour) {
  const scaledContour = convertPointArrayToArray(contour.getPolygonScaled());
  if (scaledContour.length < 2) return;
  const centroidPosition = contour.getCentroidScaled();
  const color = resolveColor(contour);
  const opacity = getContourDisplayOpacity(contour);

  const contourMoved = moveDisplayContour(
    { label: contour.getProps().label, id: contour.getProps().id },
    scaledContour,
    centroidPosition,
  );

  const start = scaledContour[0];
  const end = scaledContour[1];
  const head = appendArrow(group, start, end, color, opacity);
  if (!head) return;

  if (isSelected(contour)) {
    appendPath(
      group,
      [start, head.shaftEnd],
      d3.hsl(color).brighter(1),
      1,
      CONTOUR_STROKE,
    );
    appendSelectionHandle(group, centroidPosition, color, contourMoved);
  }
}

function drawText(group, contour) {
  const scaledContour = convertPointArrayToArray(contour.getPolygonScaled());
  if (!scaledContour || !scaledContour.length) return;
  const { text } = contour.getProps();
  if (!text) return;
  moveDisplayContour(
    { label: contour.getProps().label, id: contour.getProps().id },
    scaledContour,
    null,
  );
  const [x, y] = scaledContour[0];
  const color = resolveColor(contour);
  const selected = isSelected(contour);
  const textEl = group
    .append('text')
    .attr('x', x)
    .attr('y', y)
    .attr('fill', selected ? 'yellow' : color)
    .attr('font-weight', selected ? 'bold' : 'normal')
    .attr('original-font-size', ANNOTATION_FONT)
    .attr('font-size', `${ANNOTATION_FONT}px`)
    .attr('text-anchor', 'middle')
    .attr('dominant-baseline', 'middle')
    .text(text);
  if (selected) {
    textEl
      .attr('stroke', 'white')
      .attr('stroke-width', 0.75)
      .attr('vector-effect', 'non-scaling-stroke')
      .attr('paint-order', 'stroke'); // outline sits behind the glyph fill
  }
}

function drawByType(group, contour, displayNumber = -1, measurement = null, badgesGroup = group) {
  switch (contour.getProps().type) {
    case ContourDefinitions.ContourType.PolygonClosedContour:
      drawClosedPolygon(group, contour, displayNumber, measurement, badgesGroup);
      break;
    case ContourDefinitions.ContourType.PolygonOpenContour:
      drawOpenPolygon(group, contour);
      break;
    case ContourDefinitions.ContourType.LineContour:
      drawLine(group, contour, displayNumber, measurement, badgesGroup);
      break;
    case ContourDefinitions.ContourType.PointContour:
      drawPoint(group, contour);
      break;
    case ContourDefinitions.ContourType.AnnotationArrowContour:
      drawArrow(group, contour);
      break;
    case ContourDefinitions.ContourType.AnnotationTextContour:
      drawText(group, contour);
      break;
    default:
      break;
  }
}

// --- value strings and formatting ------------------------------------------

function formatVal(v, decimals = 1) {
  if (v == null || !Number.isFinite(v)) return '—';
  return (Math.round(v * 10 ** decimals) / 10 ** decimals).toFixed(decimals);
}

function buildPolygonValueLine(contour, displayNumber) {
  const areaRW = Math.round(contour.getAreaRealWorldvalue()) / 100;
  const siStat = contour.getSignalIntensityStatistic();
  return (
    `${displayNumber}. ${areaRW} cm²`
    + ` Mean: ${formatVal(siStat.mean)}±${formatVal(siStat.stdv)}`
    + ` Med: ${formatVal(siStat.median)}`
    + ` Min/Max: ${formatVal(siStat.min, 0)}/${formatVal(siStat.max, 0)}`
  );
}

function buildLineValueLine(contour, displayNumber) {
  const distanceRW = Math.round(contour.getDistanceRealWorldvalue() * 10) / 10;
  return `${displayNumber}. ${distanceRW} mm`;
}

function buildMaskValueLine(contour, displayNumber, maskName, maskStats, siStat, areaRW) {
  let line = `${displayNumber}. ${areaRW} cm²`;
  if (maskStats && maskName) {
    line += ` [${maskName}] Mean: ${formatVal(maskStats.mean)}±${formatVal(maskStats.stdv)}`;
    line += ` Med: ${formatVal(maskStats.median)}`;
    line += ` Min/Max: ${formatVal(maskStats.min, 0)}/${formatVal(maskStats.max, 0)}`;
  }
  else if (siStat) {
    line += ` Mean: ${formatVal(siStat.mean)}±${formatVal(siStat.stdv)}`;
    line += ` Med: ${formatVal(siStat.median)}`;
    line += ` Min/Max: ${formatVal(siStat.min, 0)}/${formatVal(siStat.max, 0)}`;
  }
  return line;
}

// --- interactions: hover / click / pin --------------------------------------

let leaveTimeout = null;

function handleHoverContour(key) {
  if (leaveTimeout) {
    clearTimeout(leaveTimeout);
    leaveTimeout = null;
  }
  hoveredContourKey.value = key;
}

function handleLeaveContour(key) {
  if (leaveTimeout) clearTimeout(leaveTimeout);
  leaveTimeout = setTimeout(() => {
    if (hoveredContourKey.value === key) {
      hoveredContourKey.value = null;
    }
  }, 140);
}

function handleTogglePinContour(key) {
  if (pinnedContourKeys.value.has(key)) {
    pinnedContourKeys.value.delete(key);
  }
  else {
    pinnedContourKeys.value.add(key);
  }
  pinnedContourKeys.value = new Set(pinnedContourKeys.value);
}

function keepHover(key) {
  if (leaveTimeout) {
    clearTimeout(leaveTimeout);
    leaveTimeout = null;
  }
  hoveredContourKey.value = key;
}

function releaseHover(key) {
  if (leaveTimeout) clearTimeout(leaveTimeout);
  leaveTimeout = setTimeout(() => {
    if (hoveredContourKey.value === key) {
      hoveredContourKey.value = null;
    }
  }, 140);
}

function isPinned(key) {
  return pinnedContourKeys.value.has(key);
}

function closeCard(key) {
  if (leaveTimeout) clearTimeout(leaveTimeout);
  pinnedContourKeys.value.delete(key);
  pinnedContourKeys.value = new Set(pinnedContourKeys.value);
  if (hoveredContourKey.value === key) {
    hoveredContourKey.value = null;
  }
}

const activeMicroCards = computed(() => {
  if (!showMeasurementBadges.value || moveContour.value?.label != null) {
    return [];
  }
  return currentMeasurements.value.filter(
    m => (hoveredContourKey.value === m.key || pinnedContourKeys.value.has(m.key)),
  );
});

function getCardPositionStyle(m) {
  const _mat = imageToScreenMatrix.value;
  if (!m.imagePos || !_mat) return { display: 'none' };
  const [sx, sy] = imageToScreen(m.imagePos.x, m.imagePos.y);

  const hasStats = Boolean(m.maskStats || m.stats);
  const cardW = hasStats ? 160 : 110;
  const cardH = hasStats ? 34 : 24;

  const maxW = canvasWidth.value || 500;
  const maxH = canvasHeight.value || 500;

  let left;
  let top;

  if (m.type === 'line') {
    left = sx - cardW / 2;
    top = sy - cardH - 8;
    if (top < 6) {
      top = sy + 14;
    }
  }
  else {
    left = sx;
    top = sy - cardH - 12;
    if (top < 6) {
      top = sy + 14;
    }
  }

  if (left < 6) {
    left = 6;
  }
  else if (left + cardW > maxW - 6) {
    left = Math.max(6, maxW - cardW - 6);
  }

  return {
    left: `${Math.round(left)}px`,
    top: `${Math.round(top)}px`,
  };
}

// --- main render -----------------------------------------------------------

function createOverlay() {
  const root = d3.select(overlay.value);
  if (root.empty() || !root.node()) return;

  formattedContourValues.value = '';
  emit('updateContourFormat', '');
  root.selectAll('svg').remove();

  if (!matrixReady.value) return;

  const contours = currentDrawingContours.value;
  if (!contours) {
    currentMeasurements.value = [];
    emit('updateMeasurements', []);
    return;
  }

  const svg = root
    .append('svg')
    .attr('width', '100%')
    .attr('height', '100%')
    .attr('pointer-events', 'none')
    .style('pointer-events', 'none')
    .style('opacity', showContours.value ? 1 : 0);
  // All contour geometry lives in this group in image-pixel coordinates; the
  // group carries the (rotation-aware) image->screen matrix in applyTransform.
  const group = svg.append('g').attr('class', 'contour-root');
  const geometryGroup = group
    .append('g')
    .attr('class', 'contour-geometry-layer')
    .attr('pointer-events', 'none')
    .style('pointer-events', 'none');
  const badgesGroup = group
    .append('g')
    .attr('class', 'contour-badges-layer')
    .attr('pointer-events', showMeasurementBadges.value ? 'auto' : 'none')
    .style('pointer-events', showMeasurementBadges.value ? 'auto' : 'none')
    .style('display', showMeasurementBadges.value ? null : 'none');

  const visibleGenerated = visibleGeneratedContours.value;

  // 1) generated (non-editable) contours first, so editable ones draw on top.

  for (const [label, ids] of Object.entries(contours)) {
    // SaxLvChord: draw every segment as a plain line when the label is visible.
    if (label === ContourDefinitions.ContourLabel.SaxLvChord) {
      if (visibleGenerated.includes(label)) {
        for (const contour of Object.values(ids)) {
          if (
            contour.getProps().type
            === ContourDefinitions.ContourType.LineContour
          ) {
            drawLine(geometryGroup, contour, -1, null, badgesGroup);
          }
        }
      }

      continue;
    }

    for (const [id, contour] of Object.entries(ids)) {
      if (
        contour.getProps().userEditable
        || !isDisplayedGeneratedContour({ label, id })
      ) {
        continue;
      }
      drawByType(geometryGroup, contour, -1, null, badgesGroup);
    }
  }

  // 2) editable contours + value strings.
  let displayValueCounter = 1;
  let displayValueString = '';
  const measurementsList = [];

  for (const [label, ids] of Object.entries(contours)) {
    for (const [id, contour] of Object.entries(ids)) {
      if (!contour.getProps().userEditable) {
        continue;
      }
      if (
        hideDrawingContour.value
        && hideDrawingContour.value.label === label
        && hideDrawingContour.value.id === id
      ) {
        continue;
      }
      if (hiddenUserEditableContours.value.includes(label)) {
        continue;
      }
      if (
        hideDrawingContourLabel.value
        && hideDrawingContourLabel.value === label
      ) {
        continue;
      }

      let displayNumber = -1;
      let measurement = null;

      if (label === ContourDefinitions.ContourLabel.GeneralLine) {
        displayNumber = displayValueCounter;
        displayValueCounter += 1;
        const distanceRW = Math.round(contour.getDistanceRealWorldvalue() * 10) / 10;
        const scaledContour = convertPointArrayToArray(contour.getPolygonScaled());
        let mx = 0;
        let my = 0;
        if (scaledContour.length >= 2) {
          mx = (scaledContour[0][0] + scaledContour[scaledContour.length - 1][0]) / 2;
          my = (scaledContour[0][1] + scaledContour[scaledContour.length - 1][1]) / 2;
        }
        measurement = {
          key: `${label}_${id}`,
          id,
          label,
          number: displayNumber,
          color: resolveColor(contour),
          type: 'line',
          name: `Distance #${displayNumber}`,
          primaryValue: `${distanceRW} mm`,
          distanceRW,
          imagePos: { x: mx, y: my },
        };
        measurementsList.push(measurement);
        displayValueString += `${buildLineValueLine(contour, displayNumber)}\n`;
      }
      else if (label === ContourDefinitions.ContourLabel.GeneralPolygon) {
        displayNumber = displayValueCounter;
        displayValueCounter += 1;
        const areaRW = Math.round(contour.getAreaRealWorldvalue()) / 100;
        const siStat = contour.getSignalIntensityStatistic();
        const primaryValue = (siStat && Number.isFinite(siStat.mean))
          ? `μSI: ${formatVal(siStat.mean)} ± ${formatVal(siStat.stdv)}`
          : `${areaRW} cm²`;
        const scaledContour = convertPointArrayToArray(contour.getPolygonScaled());
        const topRight = getTopRightContourPoint(scaledContour) || contour.getCentroidScaled() || { x: 0, y: 0 };
        measurement = {
          key: `${label}_${id}`,
          id,
          label,
          number: displayNumber,
          color: resolveColor(contour),
          type: 'polygon',
          name: `Freehand ROI #${displayNumber}`,
          primaryValue,
          areaRW,
          stats: siStat,
          imagePos: { x: topRight.x, y: topRight.y },
        };
        measurementsList.push(measurement);
        displayValueString += `${buildPolygonValueLine(contour, displayNumber)}\n`;
      }
      else if (label === ContourDefinitions.ContourLabel.MaskMeasurement) {
        displayNumber = displayValueCounter;
        displayValueCounter += 1;
        const areaRW = Math.round(contour.getAreaRealWorldvalue()) / 100;
        let maskStats = null;
        const maskName = selectedMaskType.value || '';
        const biomarkerContainer = analysisStore?.getBiomarkerContainer?.();
        if (maskName && biomarkerContainer) {
          const maskObject = biomarkerContainer.getMaskByImageUIDAndType(curImageID.value, maskName);
          if (maskObject) {
            maskStats = contour.getMaskPixelValueStatistic(maskObject.getData());
          }
        }
        const siStat = contour.getSignalIntensityStatistic();
        const stat = maskStats || siStat;
        const primaryValue = (stat && Number.isFinite(stat.mean))
          ? `μSI: ${formatVal(stat.mean)} ± ${formatVal(stat.stdv)}`
          : `${areaRW} cm²`;
        const scaledContour = convertPointArrayToArray(contour.getPolygonScaled());
        const topRight = getTopRightContourPoint(scaledContour) || contour.getCentroidScaled() || { x: 0, y: 0 };
        measurement = {
          key: `${label}_${id}`,
          id,
          label,
          number: displayNumber,
          color: resolveColor(contour),
          type: 'mask',
          name: `Mask ROI #${displayNumber}`,
          maskName,
          primaryValue,
          areaRW,
          stats: siStat,
          maskStats,
          imagePos: { x: topRight.x, y: topRight.y },
        };
        measurementsList.push(measurement);
        displayValueString += `${buildMaskValueLine(contour, displayNumber, maskName, maskStats, siStat, areaRW)}\n`;
      }

      drawByType(geometryGroup, contour, displayNumber, measurement, badgesGroup);
    }
  }

  currentMeasurements.value = measurementsList;
  emit('updateMeasurements', measurementsList);
  formattedContourValues.value = displayValueString;
  emit('updateContourFormat', formattedContourValues.value);

  applyTransform();
}

// Applies the image->screen affine to the contour group and keeps radii/fonts
// at constant on-screen size. Strokes use `non-scaling-stroke`, so they are
// already constant and need no counter-scaling here.
function applyTransform() {
  const group = d3.select(overlay.value).select('g.contour-root');
  applyGroupTransform(group, {
    overlayTransform: overlayTransform.value,
    scaleFactor: scaleFactor.value,
    imageToScreenMatrix: imageToScreenMatrix.value,
  });
}

// --- lifecycle + reactivity ------------------------------------------------

let unsubscribeContourChange = null;

onMounted(() => {
  // Redraw only when the change touches the image shown in this view
  // (batch/deleteAll events carry no UIDs -> always redraw).
  unsubscribeContourChange = contourEventStore.on((event) => {
    if (event.UIDs.length && !event.UIDs.includes(curImageID.value)) return;
    if (matrixReady.value) {
      createOverlay();
    }
  });
  if (matrixReady.value) {
    createOverlay();
  }
});

onUnmounted(() => {
  if (unsubscribeContourChange) {
    unsubscribeContourChange();
    unsubscribeContourChange = null;
  }
});

watch(
  matrixReady,
  (ready) => {
    if (ready) {
      createOverlay();
    }
    else {
      const root = d3.select(overlay.value);
      if (!root.empty() && root.node()) {
        root.selectAll('svg').remove();
      }
    }
  },
  { immediate: true },
);

// Camera pan/zoom/rotate: only re-apply the transform (cheap, no rebuild).
watch(
  () => overlayTransform.value,
  () => {
    if (matrixReady.value) {
      requestAnimationFrame(applyTransform);
    }
  },
);

watch(() => visibleGeneratedContours.value, createOverlay, { deep: false });
watch(() => visibleGeneratedContourIds.value, createOverlay, { deep: false });
watch(() => hiddenUserEditableContours.value, createOverlay, { deep: false });
watch(() => hideDrawingContour.value, createOverlay, { deep: false });
watch(() => hideDrawingContourLabel.value, createOverlay, { deep: false });
watch(
  () => selectedContour.value,
  () => {
    hoveredContourKey.value = null;
    createOverlay();
  },
  { deep: false },
);
watch(
  () => moveContour.value,
  () => {
    hoveredContourKey.value = null;
    createOverlay();
  },
  { deep: false },
);
watch(() => saxLvTransmuralityOffsetEndo.value, createOverlay, { deep: false });
watch(() => saxLvTransmuralityOffsetEpi.value, createOverlay, { deep: false });
watch(() => curImageID.value, createOverlay, { deep: false });
watch(() => selectedMaskType.value, createOverlay, { deep: false });
watch(
  () => showContours.value,
  (val) => {
    const svgs = d3.select(overlay.value).selectAll('svg');
    if (val && svgs.empty() && matrixReady.value) {
      createOverlay();
      return;
    }
    svgs
      .interrupt()
      .style('opacity', val ? 1 : 0);
  },
);
watch(
  () => showMeasurementBadges.value,
  (val) => {
    if (!val) {
      if (leaveTimeout) clearTimeout(leaveTimeout);
      hoveredContourKey.value = null;
      pinnedContourKeys.value.clear();
      pinnedContourKeys.value = new Set();
    }
    const bg = d3.select(overlay.value).select('g.contour-badges-layer');
    if (!bg.empty()) {
      bg.style('display', val ? null : 'none')
        .style('pointer-events', val ? 'auto' : 'none')
        .attr('pointer-events', val ? 'auto' : 'none');
    }
  },
);
watch(() => localizerDir2D.value, createOverlay, { deep: false });
</script>

<script>
export default {
  name: 'UIDDisplayContours',
};
</script>

<template>
  <div class="overlay">
    <div
      ref="overlay" style="
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        height: 100%;
        will-change: transform;
      "
    />

    <!-- Interactive Micro-Cards Layer -->
    <div v-show="showContours && showMeasurementBadges" class="micro-cards-layer">
      <transition-group name="card-pop">
        <div
          v-for="m in activeMicroCards"
          :key="m.key"
          class="measurement-micro-card"
          :class="{ 'is-pinned': isPinned(m.key) }"
          :style="getCardPositionStyle(m)"
          @mouseenter="keepHover(m.key)"
          @mouseleave="releaseHover(m.key)"
          @click.stop="handleTogglePinContour(m.key)"
        >
          <!-- Main Line: Number, Value, Mask tag, Unpin/Close -->
          <div class="card-line-main">
            <span class="card-num" :style="{ color: m.color }">#{{ m.number }}</span>
            <span class="card-val">{{ m.primaryValue }}</span>
            <span v-if="m.maskName" class="card-tag">{{ m.maskName }}</span>
            <button
              v-if="isPinned(m.key)"
              class="card-unpin-btn"
              title="Unpin"
              @click.stop="closeCard(m.key)"
            >
              ✕
            </button>
          </div>

          <!-- Stats Line: Compact single line -->
          <div v-if="m.maskStats" class="card-line-stats">
            <span v-if="m.areaRW"><span class="stat-lbl">Area</span> {{ m.areaRW }} cm²</span>
            <span v-if="m.areaRW" class="stat-dot">|</span>
            <span><span class="stat-lbl">Med SI</span> {{ formatVal(m.maskStats.median) }}</span>
          </div>
          <div v-else-if="m.stats" class="card-line-stats">
            <span v-if="m.areaRW"><span class="stat-lbl">Area</span> {{ m.areaRW }} cm²</span>
            <span v-if="m.areaRW" class="stat-dot">|</span>
            <span><span class="stat-lbl">Med SI</span> {{ formatVal(m.stats.median) }}</span>
          </div>
        </div>
      </transition-group>
    </div>
  </div>
</template>

<style scoped>
.overlay {
  position: absolute;
  pointer-events: none;
  z-index: 20;
  width: 100%;
  height: 100%;
}

.micro-cards-layer {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 100;
}

.measurement-micro-card {
  position: absolute;
  pointer-events: auto;
  width: max-content;
  max-width: 220px;
  background: rgba(15, 15, 15, 0.94);
  backdrop-filter: var(--glass-backdrop-blur, blur(20px));
  -webkit-backdrop-filter: var(--glass-backdrop-blur, blur(20px));
  border: 1px solid var(--md-border, #404040);
  border-radius: 4px;
  box-shadow: var(--glass-shadow, 0 4px 12px rgba(0, 0, 0, 0.6));
  padding: 4px 7px;
  color: var(--md-text-primary, #e5e5e5);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  user-select: none;
  z-index: 100;
  display: flex;
  flex-direction: column;
  gap: 2px;
  cursor: pointer;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;
}

.measurement-micro-card.is-pinned {
  border-color: var(--md-primary, #00bcd4);
  box-shadow:
    0 0 0 1px var(--md-primary-hover, rgba(0, 188, 212, 0.4)),
    0 4px 12px rgba(0, 0, 0, 0.6);
}

.card-line-main {
  display: flex;
  align-items: center;
  gap: 5px;
  line-height: 1.1;
}

.card-num {
  font-size: 0.68rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.card-val {
  font-size: 0.72rem;
  font-weight: 600;
  color: var(--md-text-primary, #ffffff);
  font-variant-numeric: tabular-nums;
}

.card-tag {
  font-size: 0.52rem;
  font-weight: 500;
  color: var(--md-on-primary-container, #9ae6ff);
  background: var(--md-primary-container, #004d5a);
  border-radius: 2px;
  padding: 0 3px;
  white-space: nowrap;
}

.card-unpin-btn {
  background: transparent;
  border: none;
  color: var(--md-text-secondary, #a3a3a3);
  cursor: pointer;
  padding: 0 2px;
  margin-left: auto;
  font-size: 0.55rem;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.1s ease;
}

.card-unpin-btn:hover {
  color: var(--md-error, #ef5350);
}

.card-line-stats {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.58rem;
  color: var(--md-text-secondary, #a3a3a3);
  font-variant-numeric: tabular-nums;
  line-height: 1.1;
  white-space: nowrap;
}

.stat-lbl {
  color: var(--md-on-surface-variant, #a3a3a3);
  font-weight: 500;
}

.stat-dot {
  color: var(--md-outline, #404040);
  font-size: 0.5rem;
}

/* Card Pop Animation */
.card-pop-enter-active,
.card-pop-leave-active {
  transition:
    opacity 0.12s ease,
    transform 0.12s ease;
}

.card-pop-enter-from,
.card-pop-leave-to {
  opacity: 0;
  transform: scale(0.96) translateY(2px);
}
</style>
